import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcBinSizeService, UpdateStcBinSizeRequest } from '../../../../../services/stcBinSizeService';
import { StcBinSizeDto } from '../../../../../types/stcBinSize';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { BinSizeSidePanel } from './BinSizeSidePanel';
import { BinSizeCreateModal } from './BinSizeCreateModal';

interface BinSizeDataTableProps {
  refreshTrigger?: number;
}

export const BinSizeDataTable: React.FC<BinSizeDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<StcBinSizeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing
  const inlineEditConfig = useInlineEdit<StcBinSizeDto>({
    getRowId: (row) => row.id,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateStcBinSizeRequest = {
        description1: (editedData.description1 as string) || '',
        width: (editedData.width as number) || 0,
        depth: (editedData.depth as number) || 0,
        height: (editedData.height as number) || 0,
        totalVolume: (editedData.totalVolume as number) || 0,
        unlimitedYesNo: (editedData.unlimitedYesNo as boolean) || false,
        externalSystemID: editedData.externalSystemID as number | undefined
      };
      await stcBinSizeService.update(
        _originalRow.companyNo,
        _originalRow.binSizeNo,
        request
      );
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || 'Feil ved lagring')
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const binSizeData = await stcBinSizeService.getCurrent().catch(() =>
        stcBinSizeService.getAll().catch(() => [])
      );
      setData(binSizeData);
    } catch (err: any) {
      console.error('Error loading bin size data:', err);
      setError('Kunne ikke laste lokasjonsstørrelser. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  // Column factory
  const columnFactory = createColumnFactory<StcBinSizeDto>();

  // Column definitions
  const columns: ColumnDefinition<StcBinSizeDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('binSizeNo', 'binSizeNo', 'Størrelse nr.', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('width', 'width', 'Bredde', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '0'
    }),

    columnFactory.createNumberColumn('depth', 'depth', 'Dybde', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '0'
    }),

    columnFactory.createNumberColumn('height', 'height', 'Høyde', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '0'
    }),

    columnFactory.createNumberColumn('totalVolume', 'totalVolume', 'Totalt volum', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '0'
    }),

    columnFactory.createBooleanColumn('unlimitedYesNo', 'unlimitedYesNo', 'Ubegrenset', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('externalSystemID', 'externalSystemID', 'Ekstern system ID', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    })
  ], []);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert('Velg minst én lokasjonsstørrelse for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length]);

  // Table actions
  const tableActions: TableAction<StcBinSizeDto>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny lokasjonsstørrelse',
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedIds.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit]);

  // Build selected rows map for BinSizeSidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'bin-sizes-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <BinSizeSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <BinSizeCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
